// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The directories a work order's `provider` and `business_owner` must come
 * from. Both are created in the provider system, not here, so the UI only
 * reads them: a dropdown on the create form, and a name for the owner id a
 * work order reads back as.
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WORK_ORDER_DIRECTORY_STALE_TIME_MS } from '../config/queryConfig';
import { listBusinessOwners, listProviders } from '../services/workOrderService';
import type { BusinessOwner, Provider } from '../types';

const NO_OWNERS: BusinessOwner[] = [];
const NO_PROVIDERS: Provider[] = [];

export function useBusinessOwners(enabled = true) {
  const query = useQuery({
    queryKey: ['work-order-business-owners'] as const,
    queryFn: listBusinessOwners,
    staleTime: WORK_ORDER_DIRECTORY_STALE_TIME_MS,
    enabled,
  });
  return {
    owners: query.data?.data ?? NO_OWNERS,
    loading: enabled && query.isLoading,
    error: query.error ? query.error.message : null,
  };
}

export function useProviders(enabled = true) {
  const query = useQuery({
    queryKey: ['work-order-providers'] as const,
    queryFn: listProviders,
    staleTime: WORK_ORDER_DIRECTORY_STALE_TIME_MS,
    enabled,
  });
  return {
    providers: query.data?.data ?? NO_PROVIDERS,
    loading: enabled && query.isLoading,
    error: query.error ? query.error.message : null,
  };
}

/** What the owner name hook answers for an id the directory does not list. */
export const UNKNOWN_OWNER = 'Unknown owner';

/**
 * A work order's `business_owner` as a name. It is the owner's upstream id on
 * the site routes; an id the directory does not know (a user since removed, or
 * a name from the deprecated routes) reads as "Unknown owner" rather than the
 * raw value [36226]. Null stays null: the caller says "Unassigned".
 */
export function useBusinessOwnerName(): (ownerId: string | null) => string | null {
  const { owners, loading } = useBusinessOwners();
  return useCallback(
    ownerId =>
      ownerId
        ? (owners.find(o => o.id === ownerId)?.name ?? (loading ? '…' : UNKNOWN_OWNER))
        : null,
    [owners, loading]
  );
}
